import { placeholdersOf, resolveMessage } from './resolve';
import {
  type AnyCatalogue,
  type CatalogueOf,
  DEFAULT_LOCALE,
  LOCALES,
  type Locale,
  type MessageParamsOf,
  type MessageValue,
  type ParameterizedKeyOf,
  type ParameterlessKeyOf,
} from './types';

/**
 * `createCatalog()` - app-local message catalogues with the platform's typing.
 *
 * The platform catalogue in `locales/` holds the strings that must be identical
 * everywhere: the guest stage model (PRD GST-036), notification copy (NTF-004),
 * receipts (PAY-022). Everything else is screen copy that belongs to the app that
 * renders it, and keeping it in that app is what stops one shared catalogue
 * turning into a merge point that every frontend has to fight over.
 *
 * What an app must NOT do is reimplement the rules, because the rules are the
 * requirement: typed keys, `{placeholder}` interpolation, EN/FR plural forms
 * through `Intl.PluralRules`, English as the fallback, and a completeness check
 * that fails CI rather than shipping a silent English string (PRD NFR-006,
 * tech-stack section 12). So this factory hands all of that back:
 *
 *     // apps/guest-web/src/i18n/index.ts
 *     const en = {
 *       'cart.title': 'Your basket',
 *       'cart.items': { one: '{count} item', other: '{count} items' },
 *       'cart.deliverTo': 'Deliver to {apartment}',
 *     } as const;
 *
 *     const fr = {
 *       'cart.title': 'Votre panier',
 *       'cart.items': { one: '{count} article', other: '{count} articles' },
 *       'cart.deliverTo': 'Livraison a {apartment}',
 *     } as const satisfies CatalogueOf<typeof en>;
 *
 *     export const guestCatalog = createCatalog({ en, fr });
 *
 *     guestCatalog.t('fr', 'cart.items', { count: 3 });   // "3 articles"
 *     guestCatalog.t('fr', 'cart.title');                  // no params: legal
 *     guestCatalog.t('fr', 'cart.deliverTo');              // compile error
 *     guestCatalog.t('fr', 'cart.titel');                  // compile error
 *
 * and the app's completeness test is three lines:
 *
 *     it('is complete in every locale', () => {
 *       expect(checkCatalogCompleteness(guestCatalog).problems).toEqual([]);
 *     });
 *
 * English is the source of truth for the key set in an app catalogue exactly as
 * it is for the platform one: `fr` is typed `CatalogueOf<typeof en>`, so a missing
 * key, an extra key or a key that changed plural-ness is a compile error before
 * the test ever runs.
 */

export interface Catalog<TSource extends AnyCatalogue> {
  /** Every shipped locale's catalogue, keyed by locale. */
  readonly catalogues: Readonly<Record<Locale, CatalogueOf<TSource>>>;
  /** The source (English) catalogue - the key set of record. */
  readonly source: TSource;
  /** Every key, in declaration order. Useful for tests and for tooling. */
  readonly keys: ReadonlyArray<keyof TSource & string>;
  /** Resolve a key in a locale, falling back through the chain to English. */
  t: {
    <K extends ParameterlessKeyOf<TSource>>(locale: Locale, key: K): string;
    <K extends ParameterizedKeyOf<TSource>>(
      locale: Locale,
      key: K,
      params: MessageParamsOf<TSource, K>,
    ): string;
  };
  /** Bound translator for a surface that already knows its locale. */
  translatorFor(locale: Locale): {
    <K extends ParameterlessKeyOf<TSource>>(key: K): string;
    <K extends ParameterizedKeyOf<TSource>>(key: K, params: MessageParamsOf<TSource, K>): string;
  };
}

/**
 * Builds a typed catalogue from one catalogue per locale. `en` is required and is
 * the key set of record; every other locale is typed against it.
 */
export function createCatalog<const TSource extends AnyCatalogue>(
  locales: { readonly en: TSource } & {
    readonly [L in Exclude<Locale, 'en'>]: CatalogueOf<TSource>;
  },
): Catalog<TSource> {
  const catalogues = locales as unknown as Readonly<Record<Locale, CatalogueOf<TSource>>>;
  const source = locales.en;
  const keys = Object.keys(source) as Array<keyof TSource & string>;

  const translate = (
    locale: Locale,
    key: keyof TSource,
    params?: Readonly<Record<string, string | number>>,
  ): string =>
    resolveMessage<TSource>(
      catalogues as Readonly<Record<Locale, Readonly<Record<keyof TSource, MessageValue>>>>,
      locale,
      key,
      params,
    );

  return {
    catalogues,
    source,
    keys,
    t: translate as Catalog<TSource>['t'],
    translatorFor: (locale: Locale) =>
      ((key: keyof TSource, params?: Readonly<Record<string, string | number>>) =>
        translate(locale, key, params)) as ReturnType<Catalog<TSource>['translatorFor']>,
  };
}

// ---------------------------------------------------------------------------
// Completeness - the CI check, as a pure function so an app's test is one line
// and no test framework leaks into the runtime bundle (tech-stack section 11).
// ---------------------------------------------------------------------------

export interface CompletenessReport {
  /** Empty when the catalogue is complete. Each entry names the locale and key. */
  readonly problems: readonly string[];
  readonly missing: readonly string[];
  readonly extra: readonly string[];
  readonly empty: readonly string[];
  readonly pluralShapeMismatch: readonly string[];
  readonly placeholderMismatch: readonly string[];
}

/**
 * Runs every rule the platform catalogue is held to (PRD NFR-006):
 *
 *  - every source key present in every shipped locale;
 *  - no locale declaring a key the source does not have;
 *  - no empty or whitespace-only value;
 *  - plural-ness identical to the source, with `one` and `other` supplied;
 *  - exactly the placeholders the source string uses - a translation that drops
 *    `{window}` renders "Order confirmed - arriving ," to a guest, and this is
 *    the check that stops it.
 */
export function checkCatalogCompleteness<TSource extends AnyCatalogue>(
  catalog: Catalog<TSource>,
): CompletenessReport {
  const missing: string[] = [];
  const extra: string[] = [];
  const empty: string[] = [];
  const pluralShapeMismatch: string[] = [];
  const placeholderMismatch: string[] = [];

  const sourceKeys = catalog.keys;

  for (const locale of LOCALES) {
    const catalogue = catalog.catalogues[locale] as Readonly<Record<string, MessageValue>>;
    if (catalogue === undefined) {
      missing.push(`${locale}: no catalogue supplied`);
      continue;
    }

    for (const key of Object.keys(catalogue)) {
      if (!(key in catalog.source)) extra.push(`${locale}.${key}`);
    }

    for (const key of sourceKeys) {
      const value = catalogue[key];
      if (value === undefined) {
        missing.push(`${locale}.${key}`);
        continue;
      }

      const sourceValue = catalog.source[key] as MessageValue;
      const sourceIsPlural = typeof sourceValue !== 'string';
      const valueIsPlural = typeof value !== 'string';
      if (sourceIsPlural !== valueIsPlural) {
        pluralShapeMismatch.push(`${locale}.${key}`);
        continue;
      }

      if (valueIsPlural) {
        if (!value.one?.trim()) empty.push(`${locale}.${key}.one`);
        if (!value.other?.trim()) empty.push(`${locale}.${key}.other`);
        for (const [form, text] of Object.entries(value)) {
          if (typeof text === 'string' && text.trim().length === 0) {
            empty.push(`${locale}.${key}.${form}`);
          }
        }
      } else if (value.trim().length === 0) {
        empty.push(`${locale}.${key}`);
      }

      const expected = [...placeholdersOf(sourceValue)].sort().join(',');
      const actual = [...placeholdersOf(value)].sort().join(',');
      if (expected !== actual) {
        placeholderMismatch.push(`${locale}.${key}: expected {${expected}} got {${actual}}`);
      }
    }
  }

  const dedupedEmpty = [...new Set(empty)];

  return {
    problems: [
      ...missing,
      ...extra,
      ...dedupedEmpty,
      ...pluralShapeMismatch,
      ...placeholderMismatch,
    ],
    missing,
    extra,
    empty: dedupedEmpty,
    pluralShapeMismatch,
    placeholderMismatch,
  };
}

/**
 * Throws with every problem listed. For a caller that wants the check somewhere
 * other than a test - a build script, a dev-mode boot assertion.
 */
export function assertCatalogComplete<TSource extends AnyCatalogue>(
  catalog: Catalog<TSource>,
  label = 'catalog',
): void {
  const report = checkCatalogCompleteness(catalog);
  if (report.problems.length > 0) {
    throw new Error(
      `${label} is incomplete (${report.problems.length} problem(s)):\n  ${report.problems.join('\n  ')}`,
    );
  }
}

/** The locale a catalogue falls back to. Exported so an app never re-declares it. */
export const CATALOG_FALLBACK_LOCALE: Locale = DEFAULT_LOCALE;
